import React, { useMemo, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { getProblemTransitionVariants, getRevealProps } from "../../utils/pageTransition";
import { useNavigate } from "react-router-dom";
import { Capacitor } from '@capacitor/core';
import { triggerHaptic } from '../../utils/haptics';
import { FaPencilAlt } from "react-icons/fa";
import { FaRegLightbulb, FaRegFileAlt } from "react-icons/fa";
import { FiRefreshCw, FiShield, FiTrendingUp, FiRepeat, FiChevronRight } from "react-icons/fi";
// --- CUSTOM HOOKS & UTILS ---
import { useWindowSize } from "../../hooks/useWindowSize";
import { useProblemSolver } from "../../hooks/useProblemSolver";
import { useOpenProblemFlow } from "../../hooks/useOpenProblemFlow";
import { useAudio } from "../../hooks/useAudio";
import { useAiChat } from "../../hooks/useAiChat";
import { useAndroidSafeArea } from "../../hooks/useAndroidSafeArea";
import { RenderMarkdownWithLatex } from "../../utils/utils";
import orzelLogo from "../../img/orzel_logo_192.png";
// --- COMPONENTS ---
import PageTestPage from "../molecules/PageTestPage";
import { readStoredStatus } from "../molecules/Page";
import DailyGoalSuccessPopup from "../molecules/DailyGoalSuccessPopup";
import AiChatPopup from "../molecules/AiChatPopup";
import SolutionStepper from "../molecules/SolutionStepper";
import HintAndFormulas from "../molecules/HintAndFormulas";
import ScratchpadModal from "../molecules/ScratchpadModal";
import HintPopup from "../molecules/HintPopup"; 
import BackupModeWidget from "../molecules/BackupModeWidget";
import { OpenProblemDecision, OpenProblemLadder } from "../molecules/OpenProblemFlow";
import SurefireInSessionPopup from "../molecules/SurefireInSessionPopup";  // <-- DODAJ
import SessionStatsPanel from "../molecules/SessionStatsPanel";
import { SessionStatsProvider } from "../../contexts/SessionStatsContext";

// --- ANIMACJA PANELU (FEEL Z NAWIGACJI: FADE + LEKKIE UNIESIENIE) ---
// Przejście między zadaniami = getProblemTransitionVariants (wspólny moduł, spring + subtelny scale,
// spójne z nawigacją, lekko spokojniejsze). Definiowane w komponencie z useReducedMotion.
// --- ANIMACJA PRZYCISKU (TYLKO FADE) ---
const buttonTransitionVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: { duration: 0.4, ease: "easeOut" } },
    exit: { opacity: 0, transition: { duration: 0.2 } }
};
// --- ANIMACJA FEEDBACKU ODPOWIEDZI (POP / SHAKE) ---
const answerFeedbackVariants = {
    idle: { scale: 1, x: 0 },
    correct: { scale: [1, 1.06, 1], transition: { duration: 0.4, ease: "easeOut", times: [0, 0.4, 1] } },
    incorrect: { x: [0, -8, 8, -6, 6, -3, 0], transition: { duration: 0.4, ease: "easeInOut" } }
};
const SolveProblem = () => {
    const navigate = useNavigate();
    const { width } = useWindowSize();
    // --- WYKRYWANIE PLATFORMY ---
    const platform = Capacitor.getPlatform();
    const isIOS = platform === 'ios';
    const prefersReducedMotion = useReducedMotion();
    const problemTransitionVariants = getProblemTransitionVariants(prefersReducedMotion);

    // --- LOGIKA HOOKA (ŹRÓDŁO PRAWDY) ---
    const {
        currentProblem, problemsSession, answers, selectedAnswerText,
        wasLastAnswerCorrect,
        isAnswerChecked, showSolution, isHintUsed,
        userFieldElo, eloChange, problemNumber,
        
        solvedToday,      // <--- UŻYWAMY TEGO DO POSTĘPU (to jest wartość z backendu)
        dailyGoal, 
        currentStreak,       // <--- UŻYWAMY TEGO DO POSTĘPU
        handleAnswerClick, handleSolution, handleHint, handleNextProblem,
        setShowSolution, isBackupModeTriggered,
        shouldRequestReview,
        lastSubmissionResult,
        attemptProcessed   
    } = useProblemSolver();
    const playCorrectSound = useAudio('/audio/correct-answer.mp3');
    const {
        isChatOpen, chatHistory, isResponding, remainingCalls, maxCalls,
        handleOpenChat, handleSendMessage, setIsChatOpen, resetChat
    } = useAiChat(currentProblem, selectedAnswerText);
    // --- SAFE AREA LOGIC (czyste env(), inset-aware na każdej platformie) ---
    const { paddingBottom } = useAndroidSafeArea();

    // main (PageTestPage) rezerwuje juz `calc(4rem + env(safe-area-inset-bottom))` jako paddingBottom,
    // wiec stopka NIE moze ponownie dodawac env() — to dublowalo safe-area i wynosilo ja "w kosmos"
    // na telefonach z paskiem 3-przyciskowym. Tu wystarczy maly odstep nad strefa paska nawigacji.
    const stickyBottomStyle = isIOS ? '1rem' : '1.5rem';

    const fabBottomPosition = isIOS
        ? `calc(9rem + ${paddingBottom})`
        : `calc(8rem + ${paddingBottom})`;
    
    // --- STAN KOMPONENTU ---
    // Zostawiamy accountStatus tylko do PageTestPage i fallbacku daty, 
    // NIE UŻYWAMY GO JUŻ DO OBLICZEŃ POSTĘPU!
    const [accountStatus, setAccountStatus] = useState(readStoredStatus);
    
    // [USUNIĘTE] sessionSolvedCount - to psuło matematykę (dodawałeś +1 do zaktualizowanego stanu)
    
    // Overlay statystyk sesji — pokazywany w miejsce zadania BEZ nawigacji,
    // żeby SolveProblem się nie odmontowywał i zadanie nie przeskakiwało na kolejne.
    const [isStatsOpen, setIsStatsOpen] = useState(false);

    const [popupState, setPopupState] = useState({ isOpen: false, completionLevel: 0 });
    const prevSolvedCountRef = useRef(0);
    const progressContainerRef = useRef(null);
    const [maxVisibleCircles, setMaxVisibleCircles] = useState(0);
    const [showSolutionLoopTooltip, setShowSolutionLoopTooltip] = useState(false);
    const solutionLoopTooltipRef = useRef(null);
    const [sketches, setSketches] = useState({});
    const [isScratchpadOpen, setIsScratchpadOpen] = useState(false);
    
    const [isHintPopupOpen, setIsHintPopupOpen] = useState(false);
    const [showBadgeTooltip, setShowBadgeTooltip] = useState(false);

    // --- ZADANIA OTWARTE: samoocena + drabinka rozwiązania (wspólne z niepewniakami) ---
    const openFlow = useOpenProblemFlow({ currentProblem, handleAnswerClick, handleSolution });
    const { isOpen: isOpenProblem, openLadder } = openFlow;
    const badgeTooltipRef = useRef(null);


    const [surefireInSessionState, setSurefireInSessionState] = useState({
        isOpen: false, message: "", subMessage: "", progress: 0, atomName: "", unit: "surefire",
        learningTarget: 3, consolidationTarget: 2,
    });

    useEffect(() => {
        if (isAnswerChecked && lastSubmissionResult) {
            const surefireData = lastSubmissionResult.surefire_progress;
            if (surefireData && Array.isArray(surefireData)) {
                const completedAtom = surefireData.find(item => item.phase_completed === true);
                if (completedAtom) {
                    let currentProgress = completedAtom.current_progress;
                    if (!currentProgress) {
                        currentProgress = completedAtom.completion_message?.includes("Utrwalenia") ? 5 : 3;
                    }
                    const timer = setTimeout(() => {
                        setSurefireInSessionState({
                            isOpen: true,
                            message: completedAtom.completion_message || "Faza Zaliczona!",
                            subMessage: "Świetna robota! Pewniak przechodzi na wyższy poziom.",
                            progress: currentProgress,
                            atomName: completedAtom.display_name || "",
                            unit: "surefire",
                            learningTarget: 3,
                            consolidationTarget: 2,
                        });
                    }, 800);
                    return () => clearTimeout(timer);
                }
            }

            // Niepewniaki (rozszerzenie) — ten sam popup, inne teksty i panel.
            // Progi są per-niepewniak (prog_opanowania dzielone na fazy), więc
            // liczby biorą się z backendu, a nie ze skali pewniaków.
            const niepewniakData = lastSubmissionResult.niepewniak_progress;
            if (niepewniakData && Array.isArray(niepewniakData)) {
                const completed = niepewniakData.find(item => item.phase_completed === true);
                if (completed) {
                    const timer = setTimeout(() => {
                        setSurefireInSessionState({
                            isOpen: true,
                            message: completed.completion_message || "Faza zaliczona!",
                            subMessage: "",
                            progress: completed.current_progress ?? 0,
                            atomName: completed.name || "",
                            unit: "niepewniak",
                            learningTarget: completed.learning_target ?? 3,
                            consolidationTarget: completed.consolidation_target ?? 2,
                        });
                    }, 800);
                    return () => clearTimeout(timer);
                }
            }
        }
    }, [isAnswerChecked, lastSubmissionResult]);
    const handleLightbulbClick = () => {
        handleHint(); 
        setIsHintPopupOpen(true);
    };
    const handleFormulaClick = () => {
        handleHint(); 
    };
    const handleSaveSketch = (problemId, paths) => {
        setSketches(prev => ({
            ...prev,
            [problemId]: paths
        }));
    };
    useEffect(() => {
        if (!isScratchpadOpen) {
            const el = document.getElementById('scratchpad-body-lock');
            if (el) el.remove();
            document.body.style.overflow = '';
        }
    }, [isScratchpadOpen]);
    
    const isFirstSessionContext = useMemo(() => {
        if (!accountStatus) return false;
        return accountStatus.current_streak === 0; 
    }, [accountStatus]);
    // --- BADGE CONFIG ---
    const baseBadgeConfig = useMemo(() => {
        if (!currentProblem || currentProblem.problem_type !== 'base') return null;
        const reason = currentProblem.source_reason;
        switch (reason) {
            case 'repetition':
                return {
                    text: 'POWTÓRKA',
                    style: "bg-[#233855]/5 border-[#233855]/10 text-[#233855] lg:pl-8 lg:pr-6 lg:py-2",
                    icon: <FiRepeat size={11} className="opacity-80" />,
                    description: "Nasz system powtórek wykrył, że to idealny moment na odświeżenie tego typu zadania, aby utrwalić wiedzę."
                };
            case 'elo':
            default:
                return {
                    text: 'Algorytm ELO',
                    style: "bg-[#233855]/5 border-[#233855]/10 text-[#233855] lg:pl-8 lg:pr-6 lg:py-2 ",
                    icon: <FiTrendingUp size={11} className="opacity-80" />,
                    description: "To zadanie zostało dobrane dynamicznie pod Twój aktualny ranking ELO, aby zapewnić optymalne tempo rozwoju."
                };
        }
    }, [currentProblem]);
    // --- EFEKTY ---
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (badgeTooltipRef.current && !badgeTooltipRef.current.contains(e.target)) {
                setShowBadgeTooltip(false);
            }
        };
        if (showBadgeTooltip) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [showBadgeTooltip]);
    useEffect(() => {
        if (isAnswerChecked) {
            const selectedAnswerObject = answers.find(answer => answer.text === selectedAnswerText);
            if (selectedAnswerObject && selectedAnswerObject.isCorrect) {
                playCorrectSound();
                triggerHaptic("success");
            } else {
                triggerHaptic("error");
            }
        }
    }, [isAnswerChecked, answers, selectedAnswerText, playCorrectSound]);
    // Aktualizacja accountStatus po odpowiedzi (żeby PageTestPage miał świeże dane o streaku itp)
    useEffect(() => {
        if (isAnswerChecked) {
            const savedStatus = localStorage.getItem("userStatus");
            if (savedStatus) {
                setAccountStatus(JSON.parse(savedStatus));
            }
        }
    }, [isAnswerChecked]);
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (showSolutionLoopTooltip && solutionLoopTooltipRef.current && !solutionLoopTooltipRef.current.contains(e.target) && !e.target.closest('button[aria-label="Informacje o Pętli Nauki z Błędów"]')) {
                setShowSolutionLoopTooltip(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [showSolutionLoopTooltip]);
    // --- POPRAWIONA LOGIKA POSTĘPU ---
    // Używamy solvedToday i dailyGoal BEZPOŚREDNIO z hooka. 
    // solvedToday pochodzi z API i zawiera już aktualnie rozwiązane zadanie.
    const dailyGoalProgress = dailyGoal > 0
        ? Math.min(100, (solvedToday / dailyGoal) * 100 + 5)
        : 0;
    useEffect(() => {
    if (attemptProcessed && dailyGoal > 0) {
        const prevCompletionLevel = Math.floor(prevSolvedCountRef.current / dailyGoal);
        const currentCompletionLevel = Math.floor(solvedToday / dailyGoal);
        if (currentCompletionLevel > prevCompletionLevel && currentCompletionLevel <= 3) {
            // Oferta powitalna przychodzi jako nagroda za pierwszy cel — backend mówi, kiedy ją przyznał.
            const welcomeOfferExpiresAt = lastSubmissionResult?.welcome_offer_granted
                ? lastSubmissionResult.promo_offer_expires_at
                : null;
            const welcomeOfferContext = welcomeOfferExpiresAt ? lastSubmissionResult.welcome_offer_context : null;
            setPopupState({ isOpen: true, completionLevel: currentCompletionLevel, welcomeOfferExpiresAt, welcomeOfferContext });
        }
    }
    prevSolvedCountRef.current = solvedToday;
}, [attemptProcessed, solvedToday, dailyGoal]);
    useEffect(() => {
        if (currentProblem) resetChat();
    }, [problemNumber, resetChat, currentProblem]);
    useEffect(() => {
        const calculateMaxCircles = () => {
            if (progressContainerRef.current) {
                const containerWidth = progressContainerRef.current.getBoundingClientRect().width;
                const fitCount = Math.floor(containerWidth / 40);
                setMaxVisibleCircles(fitCount > 0 ? fitCount : 1);
            }
        };
        setTimeout(calculateMaxCircles, 0);
        window.addEventListener('resize', calculateMaxCircles);
        return () => window.removeEventListener('resize', calculateMaxCircles);
    }, [problemNumber]);
    // --- HELPERY UI ---
    const getVisualLengthEstimate = (text) => {
        if (!text) return 0;
        return text.replace(/\\[a-zA-Z]+|\$|{|}/g, '').length;
    };
    const gridClass = useMemo(() => {
        if (!answers) return "grid-cols-1";
        let dynamicThreshold = width < 768 ? 15 : 40;
        const isAnyAnswerLong = answers.some(answer => getVisualLengthEstimate(answer.text) > dynamicThreshold);
        return isAnyAnswerLong ? "grid-cols-1" : "grid-cols-2";
    }, [answers, width]);
    const renderEloChange = (change) => {
        // Backend oddaje zmiane po podlodze 0, wiec przy ELO 0 spadek przychodzi
        // jako 0 - nie pokazujemy zera, tak jak wczesniej nie pokazywalismy minusa.
        if (change === null || change === 0 || currentProblem?.is_mobile) return null;
        let displayChange = change;
        if (change < 0) {
            if (userFieldElo === 0) return null;
            if (userFieldElo + change < 0) displayChange = -userFieldElo;
        }
        let colorClass = "";
        if (displayChange > 0) {
            // Wzrost -> Zielony
            colorClass = "text-[#007D00]";
        } else {
            // Spadek
            // Jeśli selectedAnswerText istnieje = User wybrał odpowiedź i była błędna -> CZERWONY
            // Jeśli selectedAnswerText jest null = User kliknął rozwiązanie bez wyboru -> SZARY
            colorClass = selectedAnswerText ? "text-[#8A0000]" : "text-gray-400";
        }
        const signText = displayChange > 0 ? `+${displayChange}` : `${displayChange}`;
        
        return (
            <span className={`ml-1 font-thin transition-all duration-500 transform ${colorClass}`}>
                {signText}
            </span>
        );
    };
    const renderProgressCircles = () => {
        if (maxVisibleCircles === 0 || problemNumber === 0) return null;
        let circlesToRender = [];
        for (let i = 1; i <= problemNumber; i++) {
            const problemData = problemsSession[i - 1];
            const isCurrent = i === problemNumber;
            let circleClass = "w-4 h-4 rounded-full flex items-center justify-center text-[0.6rem] font-bold shrink-0 text-white ";
            if (isCurrent && !isAnswerChecked) {
                circleClass += "bg-[#233855] ring-2 ring-offset-2 ring-[#233855] shadow";
            } else {
                if (problemData && problemData.is_solved !== undefined) {
                    circleClass += problemData.is_solved ? "bg-[#007D00]" : "bg-[#8A0000]";
                } else {
                    circleClass += "bg-gray-400";
                }
            }
            circlesToRender.push(<div key={`circle-${i}`} className={circleClass}>{i}</div>);
        }
        if (circlesToRender.length > maxVisibleCircles) return circlesToRender.slice(-maxVisibleCircles);
        return circlesToRender;
    };
    if (!currentProblem) {
        return (
            <PageTestPage showNav={true} accountStatus={accountStatus} dailyGoalProgress={dailyGoalProgress}>
                <div className="text-center py-10"><p className="text-xl text-gray-600">Ładowanie...</p></div>
            </PageTestPage>
        );
    }
    return (
        <SessionStatsProvider value={{ isOpen: isStatsOpen, setOpen: setIsStatsOpen }}>
        <PageTestPage
            showNav={true}
            reportIssueData={{ problem_id: currentProblem.problem_id, field_id: currentProblem.field_id }}
            accountStatus={accountStatus}
            dailyGoalProgress={dailyGoalProgress}
        >
            {isStatsOpen ? (
                <SessionStatsPanel />
            ) : (
            <>
            {currentProblem && (
                <ScratchpadModal
                    isOpen={isScratchpadOpen}
                    onClose={() => setIsScratchpadOpen(false)}
                    problem={currentProblem}
                    initialPaths={sketches[currentProblem.problem_id]} 
                    onSave={handleSaveSketch}
                />
            )}
            {/* --- PRZYCISK OTWIERANIA (FAB) --- */}
            <button
                onClick={() => setIsScratchpadOpen(true)}
                className="fixed right-4 z-40 bg-[#233855] text-white p-4 rounded-full shadow-[0_4px_14px_0_rgba(0,0,0,0.39)] hover:bg-[#345081] transition-transform hover:scale-105 active:scale-95 border-2 border-[#E09F2E]"
                style={{
                    bottom: fabBottomPosition
                }}
                aria-label="Otwórz brudnopis"
            >
                <FaPencilAlt size={20} />
            </button>
            {popupState.isOpen && (
                <DailyGoalSuccessPopup
                    completionLevel={popupState.completionLevel}
                    onClose={() => setPopupState({ isOpen: false, completionLevel: 0 })}
                    isFirstSession={isFirstSessionContext}
                    shouldRequestReview={shouldRequestReview}
                    streakDay={currentStreak}
                    welcomeOfferExpiresAt={popupState.welcomeOfferExpiresAt}
                    welcomeOfferContext={popupState.welcomeOfferContext}
                    goalSolved={solvedToday}
                    goalTotal={dailyGoal}
                    onCheckOffer={() => {
                        setPopupState({ isOpen: false, completionLevel: 0 });
                        navigate("/upgrade");
                    }}
                />
            )}
            <SurefireInSessionPopup 
                isOpen={surefireInSessionState.isOpen && !popupState.isOpen}
                message={surefireInSessionState.message}
                subMessage={surefireInSessionState.subMessage}
                progress={surefireInSessionState.progress}
                atomName={surefireInSessionState.atomName}
                unit={surefireInSessionState.unit}
                learningTarget={surefireInSessionState.learningTarget}
                consolidationTarget={surefireInSessionState.consolidationTarget}
                panelLabel={surefireInSessionState.unit === 'niepewniak'
                    ? 'Przejdź do panelu niepewniaków →'
                    : 'Przejdź do panelu pewniaków →'}
                onContinue={() => setSurefireInSessionState(prev => ({ ...prev, isOpen: false }))}
                onGoToPanel={() => {
                    setSurefireInSessionState(prev => ({ ...prev, isOpen: false }));
                    navigate(surefireInSessionState.unit === 'niepewniak' ? '/niepewniaki' : '/surefires');
                }}
            />
            <div className="flex flex-col max-w-2xl mx-auto w-full flex-grow px-5 lg:px-0">

                {/* --- PANEL ZADANIA — CAŁOŚĆ (karta + rozwiązanie + akcje) PRZESUWA SIĘ RAZEM --- */}
                {/* Pager: przesuw przycina overflow-x-hidden na <main> (PageTestPage). Wrapper bez paddingu = karta wyśrodkowana, cień nietknięty. */}
                <div className="relative w-full mt-[4vh] lg:mt-[8vh]">
                <AnimatePresence mode="wait" initial={false}>
                <motion.div
                    key={currentProblem.problem_id}
                    variants={problemTransitionVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    className="w-full"
                >
                {/* --- BIAŁA KARTA (TREŚĆ ZADANIA) --- */}
                <div className="bg-white rounded-xl lg:rounded-[3rem] shadow-[4px_4px_12px_rgba(0,0,0,0.30)] border border-gray-200 pt-8 pb-6 px-5 lg:px-14 w-full overflow-hidden relative">

                    {/* --- BADGE DLA ZADAŃ TYPU BACKUP (PRIORYTET) --- */}
                    {currentProblem.problem_type === 'backup' && ( 
                        <div className={`
                            absolute top-0 left-0 z-20 px-3 py-1 rounded-br-2xl border-b border-r lg:pl-8 lg:pr-6 lg:py-2
                            flex items-center gap-1.5
                            ${currentProblem.origin === 'solution' 
                                ? "bg-[#233855]/5 border-[#233855]/10 text-[#233855]" 
                                : "bg-[#E09F2E]/10 border-[#E09F2E]/20 text-[#E09F2E]"
                            }
                        `}>
                            {currentProblem.origin === 'solution' 
                                ? <FiShield size={11} className="opacity-80" /> 
                                : <FiRefreshCw size={11} className="opacity-80" />
                            }
                            <span className="text-[8px] font-bold font-apotek tracking-[0.15em] uppercase leading-none pt-0.5">
                                {currentProblem.origin === 'solution' ? "Reality Check" : "Comeback"}
                            </span>
                        </div>
                    )}
                    {/* --- [NOWE] BADGE DLA ZADAŃ TYPU BASE --- */}
                    {currentProblem.problem_type === 'base' && baseBadgeConfig && (
                        <div 
                            ref={badgeTooltipRef}
                            onClick={() => setShowBadgeTooltip(!showBadgeTooltip)}
                            className="absolute top-0 left-0 z-30 cursor-pointer group"
                        >
                            <div className={`
                                px-3 py-1 rounded-br-2xl border-b border-r
                                flex items-center gap-1.5 transition-opacity group-hover:opacity-100
                                ${baseBadgeConfig.style}
                            `}>
                                {baseBadgeConfig.icon}
                                <span className="text-[8px] font-bold font-apotek tracking-[0.15em] uppercase leading-none pt-0.5">
                                    {baseBadgeConfig.text}
                                </span>
                            </div>
                            <AnimatePresence>
                                {showBadgeTooltip && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -5 }}
                                        animate={{ opacity: 1, y: 0, transition: { duration: 0.2, ease: [0.22, 1, 0.36, 1] } }}
                                        exit={{ opacity: 0, transition: { duration: 0.1 } }}
                                        className="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-200 shadow-xl rounded-lg p-3 z-50 text-left"
                                    >
                                        <p className="text-[10px] font-bold text-gray-800 uppercase tracking-wider mb-1 font-apotek">
                                            Dlaczego to zadanie?
                                        </p>
                                        <p className="text-xs text-gray-700 leading-relaxed font-normal">
                                            {baseBadgeConfig.description}
                                        </p>
                                        <div className="absolute -top-[5px] left-4 w-2 h-2 bg-white border-t border-l border-gray-200 transform rotate-45"></div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    )}
                    {/* --- IKONA ŻARÓWKI (POPUP HINT) --- */}
                    {currentProblem.hint && currentProblem.hint.trim() !== "" && (
                        <button
                            onClick={handleLightbulbClick}
                            className="absolute top-4 right-4 text-[#E09F2E] hover:text-[#b88226] hover:scale-110 transition-all p-2 z-10"
                            aria-label="Zobacz wskazówkę"
                        >
                            <FaRegLightbulb size={24} strokeWidth={1} className="drop-shadow-sm" />
                        </button>
                    )}
                    {/* --- WARSTWA TREŚCI (układ pionowy, bez własnej animacji) --- */}
                    <div className="w-full space-y-6">
                            {/* ELO HEADER */}
                            <div className="w-full flex justify-center items-baseline flex-wrap gap-x-3 sm:gap-x-4 gap-y-2 text-xs font-apotek lg:mt-[0.5vh]">
                                <div className="flex items-baseline gap-2">
                                    <span className="uppercase font-medium text-xs text-[#233855]/60 tracking-wide">Twoje ELO</span>
                                    <span className="text-[#233855] font-bold text-xs flex items-baseline gap-1">
                                        {userFieldElo}
                                        {(isAnswerChecked || showSolution) && renderEloChange(eloChange)}
                                    </span>
                                </div>
                                <span className="text-[#233855]/30 font-light text-xs">vs</span>
                                <div className="flex items-baseline gap-2">
                                    <span className="text-[#233855] font-bold text-xs">{currentProblem.problem_elo}</span>
                                    <span className="uppercase font-medium text-xs text-[#233855]/60 tracking-wide">ELO Zadania</span>
                                </div>
                            </div>
                            {/* PYTANIE */}
                            <p className="text-base lg:text-normal font-normal text-gray-900 text-center min-h-[50px] flex items-center justify-center lg:mt-[0.5vh]">
                                <RenderMarkdownWithLatex text={!currentProblem.is_mobile && currentProblem.points_assigned ? `(${currentProblem.points_assigned} pkt) ${currentProblem.question}` : currentProblem.question} />
                            </p>
                            {/* OBRAZEK */}
                            {currentProblem.image_link && (
                                <div className="flex justify-center py-2">
                                    <img
                                        src={
                                            currentProblem.image_link.startsWith('http')
                                                ? currentProblem.image_link
                                                : `${process.env.REACT_APP_STATIC_FILES_SOURCE || ''}/images/${currentProblem.image_link}`
                                        }
                                        alt="Ilustracja zadania"
                                        className="max-w-full h-auto rounded border"
                                    />
                                </div>
                            )}
                        {/* KARTA WZORÓW, ODPOWIEDZI, ROZWIĄZANIE — w tej samej animowanej jednostce */}
                        <div className="space-y-4 pt-1">
                        {/* KARTA WZORÓW */}
                        {(() => {
                            const hasLinkedFormulas = currentProblem.linked_formulas && currentProblem.linked_formulas.length > 0;
                            if (!hasLinkedFormulas) return null;
                            if (isHintUsed) {
                                return (
                                    <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                                        <HintAndFormulas hintText={null} formulas={currentProblem.linked_formulas} />
                                    </div>
                                );
                            }
                            return (
                                <button
                                    onClick={handleFormulaClick}
                                    className="group w-full flex items-center gap-3 bg-white border border-[#E09F2E]/40 px-4 py-3 rounded-xl shadow-sm hover:shadow-md hover:border-[#E09F2E] transition-all active:scale-[0.98]"
                                >
                                    <div className="w-9 h-9 rounded-lg bg-[#233855] flex items-center justify-center flex-shrink-0">
                                        <FaRegFileAlt size={16} className="text-[#E09F2E]" />
                                    </div>
                                    <div className="flex-1 text-left min-w-0">
                                        <span className="block text-sm font-bold text-[#233855] font-apotek leading-tight">Karta Wzorów</span>
                                        <span className="block text-xs text-gray-400 leading-tight">Otwórz wzory do zadania</span>
                                    </div>
                                    <div className="w-7 h-7 rounded-full bg-gray-100 group-hover:bg-[#233855]/10 flex items-center justify-center flex-shrink-0 transition-colors">
                                        <FiChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#233855] group-hover:translate-x-0.5 transition-all" />
                                    </div>
                                </button>
                            );
                        })()}

                        {/* ZADANIA OTWARTE: samoocena + drabinka (spójne z siatką odpowiedzi) */}
                        {isOpenProblem && (
                            <OpenProblemDecision
                                currentProblem={currentProblem}
                                flow={openFlow}
                                isAnswerChecked={isAnswerChecked}
                                wasLastAnswerCorrect={wasLastAnswerCorrect}
                                showSolution={showSolution}
                                setShowSolution={setShowSolution}
                            />
                        )}

                        {/* ODPOWIEDZI (zamknięte) */}
                        {!isOpenProblem && (
                        <div className={`grid ${gridClass} gap-2 w-full`}>
                            {answers.map((answer, index) => {
                                const isCorrectAndChecked = isAnswerChecked && answer.isCorrect;
                                const isIncorrectSelected = isAnswerChecked && !answer.isCorrect && selectedAnswerText === answer.text;
                                let buttonClass = "flex items-center justify-center rounded-lg text-[0.8rem] text-slate-100 px-3 py-1.5 font-normal tracking-wider shadow-md transition-colors duration-200 disabled:cursor-not-allowed min-h-[20px] w-full";
                                if (isCorrectAndChecked) buttonClass += " bg-[#007D00] ring-2 ring-offset-2 ring-[#007D00]";
                                else if (isIncorrectSelected) buttonClass += " bg-[#8A0000] opacity-90";
                                else if (isAnswerChecked) buttonClass += " bg-[#233855] opacity-40";
                                else buttonClass += " bg-[#233855] hover:bg-[#345081]";

                                const feedbackState = prefersReducedMotion
                                    ? "idle"
                                    : isCorrectAndChecked
                                        ? "correct"
                                        : isIncorrectSelected
                                            ? "incorrect"
                                            : "idle";
                                return (
                                    <motion.button
                                        key={index}
                                        onClick={() => handleAnswerClick(answer)}
                                        disabled={isAnswerChecked}
                                        className={buttonClass}
                                        variants={answerFeedbackVariants}
                                        animate={feedbackState}
                                        whileTap={!isAnswerChecked ? { scale: 0.96 } : undefined}
                                    >
                                        <RenderMarkdownWithLatex text={answer.text} />
                                    </motion.button>
                                );
                            })}
                        </div>
                        )}

                        {/* SEKCJA ODKUPIENIA (zamknięte — otwarte mają swój widget poza boxem) */}
                        <AnimatePresence>
                            {isAnswerChecked && isBackupModeTriggered && !isOpenProblem && (
                                <motion.div {...getRevealProps(prefersReducedMotion)}>
                                    <BackupModeWidget variant={selectedAnswerText ? 'error' : 'solution'} />
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {!isOpenProblem && !showSolution && (
                            <button
                                onClick={isAnswerChecked ? () => setShowSolution(true) : handleSolution}
                                className="w-full text-xs font-uppercase font-apotek shadow-md tracking-widest bg-[#F0F0F0] border border-gray-300 text-gray-600 hover:bg-gray-50 px-4 py-1.5 rounded-lg font-medium transition-colors"
                            >
                                ROZWIĄZANIE
                            </button>
                        )}
                        </div>
                    </div>
                </div>
                {/* --- /BIAŁA KARTA --- */}

                {/* POZA KARTĄ: rozwiązanie krok po kroku + Orzeł AI (zamknięte / podgląd otwartych) */}
                {showSolution && !(isOpenProblem && openLadder) && (
                    <div className="w-full mt-4 space-y-3 animate-in fade-in zoom-in duration-300">
                        <SolutionStepper solutionText={currentProblem.solution || "Brak rozwiązania."} />
                        <button
                            onClick={handleOpenChat}
                            disabled={isResponding}
                            className="group w-full flex items-center gap-3 bg-white border border-[#E09F2E]/40 px-4 py-3 rounded-xl shadow-sm hover:shadow-md hover:border-[#E09F2E] transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <div className="w-9 h-9 rounded-full bg-[#233855] flex items-center justify-center flex-shrink-0 overflow-hidden">
                                <img src={orzelLogo} alt="Orzeł AI" className="w-full h-full object-cover" />
                            </div>
                            <div className="flex-1 text-left min-w-0">
                                <span className="block text-sm font-bold text-[#233855] font-apotek leading-tight">Zapytaj Orła AI</span>
                                <span className="block text-xs text-gray-400 leading-tight">Wyjaśni krok po kroku</span>
                            </div>
                            <div className="w-7 h-7 rounded-full bg-gray-100 group-hover:bg-[#233855]/10 flex items-center justify-center flex-shrink-0 transition-colors">
                                <FiChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#233855] group-hover:translate-x-0.5 transition-all" />
                            </div>
                        </button>
                    </div>
                )}

                {/* POZA KARTĄ (otwarte, "Mam inaczej"): pełne rozwiązanie + decyzja finalna */}
                {isOpenProblem && openLadder && (
                    <OpenProblemLadder
                        currentProblem={currentProblem}
                        flow={openFlow}
                        isAnswerChecked={isAnswerChecked}
                        isBackupModeTriggered={isBackupModeTriggered}
                        onOpenChat={handleOpenChat}
                        isResponding={isResponding}
                    />
                )}

                {/* PRZYCISK KOLEJNE ZADANIE */}
                <AnimatePresence>
                    {isAnswerChecked && (
                        <motion.div
                            key="next-button"
                            variants={buttonTransitionVariants}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                            className="w-full pt-6 flex justify-center"
                        >
                            <button
                                onClick={handleNextProblem}
                                className="flex w-full lg:w-[25rem] justify-center rounded-lg bg-[#233855] px-3 py-2 text-xs lg:text-xs font-apotek tracking-widest text-slate-100 shadow-lg hover:bg-[#345081] focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600"
                            >
                                KOLEJNE ZADANIE
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>
                </motion.div>
                </AnimatePresence>
                </div>
                {/* --- /PANEL ZADANIA --- */}
                <div className="h-[10vh] flex-shrink-0" />
            </div>
            <div className="flex-grow" />
            {/* --- STOPKA (POSTĘP) --- */}
            <div 
                className="sticky w-full pt-3 z-30 lg:bottom-[5vh]" 
                style={{ 
                    bottom: stickyBottomStyle 
                }}
            >
                <div className="max-w-xl mx-auto space-y-5 p-2">
                    <div ref={progressContainerRef} className="flex flex-row flex-nowrap justify-center items-center space-x-2.5">
                        {/* {renderProgressCircles()} */}
                    </div>
                    {!currentProblem.is_mobile && currentProblem?.linked_matura_problems?.length > 0 && (
                        <div className="w-full text-center text-xs text-gray-500 italic">
                            Na podstawie: {currentProblem.linked_matura_problems.map(linkedPb => linkedPb.source).join(', ')}
                        </div>
                    )}
                </div>
            </div>
            <AiChatPopup
                isOpen={isChatOpen}
                onClose={() => setIsChatOpen(false)}
                chatHistory={chatHistory}
                onSendMessage={handleSendMessage}
                isResponding={isResponding}
                problemQuestion={currentProblem?.question}
                remainingCalls={remainingCalls}
                maxCalls={maxCalls}
            />
            <HintPopup
                isOpen={isHintPopupOpen}
                onClose={() => setIsHintPopupOpen(false)}
                hintText={currentProblem.hint}
            />
            </>
            )}
        </PageTestPage>
        </SessionStatsProvider>
    );
};
export default SolveProblem;
